import type { ComponentType, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ArrowRight, MailWarning } from 'lucide-react';
import { cn } from '@/lib/cn';
import { chavesDeRetidas, resumoDasRetidas } from '@/features/email/email.api';
import { CAMINHO_DAS_RETIDAS, ROTULO_DAS_RETIDAS } from '@/features/email/navegacao';
import { textoDoAviso } from '@/features/email/retidas';

/**
 * O `to` do TanStack Router é tipado contra a árvore registrada em
 * `app/router.tsx`, e esta rota ainda não está lá — o roteador pertence a outro
 * dono e a integração é uma linha que ele acrescenta (ver `routes.ts`). A
 * conversão fica num ponto só, como em `features/tickets/navigation.tsx`, e
 * some sozinha depois da integração.
 */
const LinkDeRota = Link as unknown as ComponentType<{
  to: string;
  className?: string;
  children: ReactNode;
}>;

/**
 * Como a fila entra no dia de quem atende.
 *
 * ## Por que puxado, e não empurrado
 *
 * A alternativa seria uma notificação disparada pelo evento `EmailHeldForReview`.
 * Ela tem dois defeitos que este cartão não tem: precisa ser **dispensada** (e o
 * que se dispensa uma vez se dispensa sempre), e continua exibida depois de a
 * mensagem já ter sido resolvida por outra pessoa. Um contador consultado a cada
 * abertura do painel some sozinho quando a fila esvazia — não vira ruído porque
 * não sobrevive ao trabalho feito.
 *
 * ## Por que ele só aparece quando há algo
 *
 * Um cartão permanente marcando "0 mensagens retidas" ensina o olho a pular
 * aquela região da tela, e no dia em que o número for 3 ninguém enxerga. Zero é
 * a ausência do cartão.
 *
 * ## O que o cartão diz além do número
 *
 * A **idade da mais antiga**. Três mensagens retidas hoje é operação normal;
 * três mensagens paradas há seis dias é um cliente convencido de que foi
 * ignorado. O número sozinho não distingue as duas situações.
 */
export function AvisoDeRetidas({ className }: { className?: string }) {
  const consulta = useQuery({
    queryKey: chavesDeRetidas.resumo,
    queryFn: resumoDasRetidas,
    // A fila é decidida por gente, não por segundo. Um minuto de defasagem no
    // contador é irrelevante; consultar a cada foco de janela não é.
    staleTime: 60_000,
    // Quem não tem `email.triage` recebe 403 aqui. Não é erro que mereça tela:
    // o cartão simplesmente não existe para essa pessoa.
    retry: false,
  });

  const resumo = consulta.data;
  if (!resumo || resumo.retidas === 0) return null;

  return (
    <LinkDeRota
      to={CAMINHO_DAS_RETIDAS}
      className={cn(
        'flex items-center gap-3 rounded-lg border border-warning/30 bg-warning/5 px-4 py-3',
        'transition-colors hover:bg-warning/10 focus-visible:shadow-glow focus-visible:outline-none',
        className,
      )}
    >
      <MailWarning className="h-5 w-5 flex-none text-warning" aria-hidden />
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium">{ROTULO_DAS_RETIDAS}</span>
        <span className="block text-sm text-muted-foreground">
          {textoDoAviso(resumo.retidas, resumo.maisAntigaEm)}
        </span>
      </span>
      <ArrowRight className="h-4 w-4 flex-none text-muted-foreground" aria-hidden />
    </LinkDeRota>
  );
}

/**
 * O mesmo contador como emblema, para quem cuida da navegação principal.
 *
 * `app/nav.ts` e `app/AppShell.tsx` estão com outro dono, então o emblema vem
 * pronto daqui: basta renderizá-lo ao lado do rótulo do item. Some sozinho
 * quando a fila esvazia, pela mesma razão do cartão.
 */
export function EmblemaDeRetidas() {
  const consulta = useQuery({
    queryKey: chavesDeRetidas.resumo,
    queryFn: resumoDasRetidas,
    staleTime: 60_000,
    retry: false,
  });

  const retidas = consulta.data?.retidas ?? 0;
  if (retidas === 0) return null;

  return (
    <span
      aria-label={`${retidas} mensagem(ns) retida(s)`}
      // `text-warning-foreground`, não `text-background`: sobre o amarelo do
      // tema claro o segundo dá ~2:1 de contraste, abaixo do mínimo AA. O token
      // existe exatamente para este par.
      className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-warning px-1.5 text-xs font-semibold text-warning-foreground"
    >
      {retidas > 99 ? '99+' : retidas}
    </span>
  );
}
